import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, MapPin, Rocket, Wrench, Briefcase, Kanban, Megaphone, Leaf, Presentation, Flag, Gauge, Calendar, Target } from "lucide-react";
import RealImage from "@/components/RealImage";
import { Seo } from "@/components/Seo";
import { STEM_PILLARS, TEAM } from "@/lib/data";
import { useT } from "@/lib/i18n";

const iconMap = { Wrench, Briefcase, Kanban, Megaphone, Leaf, Presentation, Flag, Gauge };

const TEAM_PHOTOS = { pietro: "/images/team/pietro.jpg", benjamin: "/images/team/benjamin.jpg", nata: "/images/team/nata.jpg", lucas: "/images/team/lucas.jpg" };
const SPONSORS = [
  { name: "Suifarma", src: "/images/sponsors/suifarma.png" },
  { name: "Catuaí", src: "/images/sponsors/catuai.jpg" },
  { name: "Hastam Motors", src: "/images/sponsors/hastam.png" },
  { name: "Investindo em Small Caps", src: "/images/sponsors/investindo.jpg" },
  { name: "CRC Planejados", src: "/images/sponsors/crc.jpg" },
  { name: "TR Usinagem", src: "/images/sponsors/tr-usinagem.jpg" },
  { name: "Saar", src: "/images/sponsors/saar.jpg" },
  { name: "Sercomtel", src: "/images/sponsors/sercomtel.jpg" },
  { name: "ImprimaPlus", src: "/images/sponsors/imprimaplus.jpg" },
  { name: "SimScale", src: "/images/sponsors/simscale.jpg" },
];
const RENDERS = [37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47].map((n) => `/images/renders/r${n}.jpg`);
const ENG_GALLERY = ["/images/engineering/eng-7.jpg", "/images/engineering/eng-25.jpg", "/images/engineering/eng-32.jpg", "/images/engineering/eng-33.jpg", "/images/engineering/eng-34.jpg", "/images/engineering/eng-35.jpg"];
const PIT_GALLERY = ["/images/pit/pit-29.jpg", "/images/pit/pit-30.jpg", "/images/pit/pit-31.jpg", "/images/pit/pit-36.jpg"];

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-24 md:py-32 relative ${className}`}><div className="container-ar relative z-10">{children}</div></section>
);
const Eyebrow = ({ n, label }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="font-mono-alt text-[10px] tracking-[0.4em] text-[#e77300]">{n}</span>
    <div className="h-px w-8 bg-[#005fca]" />
    <span className="eyebrow">{label}</span>
  </div>
);

export default function Home() {
  const t = useT();
  const pillars = [
    { key: "eng" }, { key: "ent" }, { key: "pm" }, { key: "mkt" },
    { key: "sus" }, { key: "pres" }, { key: "pit" }, { key: "race" },
  ];
  const iconByKey = { eng: Wrench, ent: Briefcase, pm: Kanban, mkt: Megaphone, sus: Leaf, pres: Presentation, pit: Flag, race: Gauge };
  const roleByTeam = { pietro: t.role_pm, benjamin: t.role_md, nata: t.role_cad, lucas: t.role_mfg };
  const bioByTeam = { pietro: t.bio_pietro, benjamin: t.bio_benjamin, nata: t.bio_nata, lucas: t.bio_lucas };
  return (
    <>
      <Seo title={t.seo_home_title} description={t.seo_home_desc} jsonLd={{
        "@context": "https://schema.org",
        "@type": "SportsTeam",
        name: "Alight Racing",
        sport: "STEM Racing",
        url: "https://alightracing.com",
        logo: "https://alightracing.com/images/misc/alight-logo.jpg",
        address: { "@type": "PostalAddress", addressLocality: "Londrina", addressRegion: "PR", addressCountry: "BR" },
      }} />
      {/* 1. HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden carbon-tex" data-testid="hero-section">
        <div className="speed-lines" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#022a67]/40 via-transparent to-[#e77300]/10 pointer-events-none" />
        <div className="container-ar relative z-10 grid lg:grid-cols-12 gap-10 py-24 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="flex items-center gap-3 mb-8">
              <span className="pulse-dot" />
              <span className="font-mono-alt text-xs tracking-[0.3em] uppercase text-[#e77300]">{t.home_live}</span>
              <div className="h-px flex-1 max-w-[100px] bg-[#005fca]/50" />
              <span className="font-mono-alt text-xs tracking-[0.3em] uppercase text-[#8ba3cc]">{t.home_origin}</span>
            </div>
            <h1 className="h1-hero text-[52px] sm:text-6xl md:text-7xl lg:text-[88px] mb-6" data-testid="hero-headline">
              {t.home_hero_h1_a}<br />
              <span className="text-[#e77300]">{t.home_hero_h1_b}</span> {t.home_hero_h1_c}
            </h1>
            <div className="flex flex-wrap gap-2 mb-6 font-display uppercase tracking-widest text-sm text-[#8ba3cc]">
              <span>{t.home_hero_line_a}</span><span className="text-[#e77300]">/</span>
              <span>{t.home_hero_line_b}</span><span className="text-[#e77300]">/</span>
              <span>{t.home_hero_line_c}</span>
            </div>
            <p className="text-white/80 text-lg leading-relaxed max-w-2xl mb-10">
              {t.home_hero_copy_a}<span className="text-[#e77300] font-semibold">{t.home_hero_copy_b}</span>{t.home_hero_copy_c}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/sponsorship" className="btn-racing primary" data-testid="hero-become-sponsor"><span>{t.home_cta_sponsor} <ArrowRight className="w-4 h-4" /></span></Link>
              <Link to="/about" className="btn-racing secondary" data-testid="hero-discover-journey"><span>{t.home_cta_journey}</span></Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <RealImage src="/images/misc/hero-car.jpg" alt="Alight Racing car render" ratio="4/3" fit="contain" padded testId="hero-image" />
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="tele-card p-3 text-center"><div className="font-mono-alt text-[10px] text-[#005fca] tracking-widest">{t.stat_category}</div><div className="font-display text-sm mt-1">{t.stat_pro}</div></div>
              <div className="tele-card p-3 text-center"><div className="font-mono-alt text-[10px] text-[#005fca] tracking-widest">{t.stat_rank}</div><div className="font-display text-sm text-[#fdb429] mt-1">{t.stat_no1}</div></div>
              <div className="tele-card p-3 text-center"><div className="font-mono-alt text-[10px] text-[#005fca] tracking-widest">{t.stat_next}</div><div className="font-display text-sm text-[#e77300] mt-1">{t.stat_sg}</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. ABOUT PREVIEW */}
      <Section id="about-preview">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow n="01" label={t.eb_about} />
            <h2 className="h2-section text-4xl md:text-5xl mb-6">{t.h_built_motorsport}</h2>
            <p className="text-white/80 text-lg leading-relaxed mb-4">{t.about_preview_1a}<span className="text-[#e77300]">{t.about_preview_1b}</span>{t.about_preview_1c}</p>
            <p className="text-white/70 text-base leading-relaxed mb-8">{t.about_preview_2}</p>
            <Link to="/about" className="btn-racing secondary" data-testid="home-learn-more"><span>{t.home_learn_more}</span></Link>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            {[
              { k: t.stat_champions, v: t.stat_champions_v, accent: "gold" },
              { k: t.stat_wf, v: t.stat_wf_v },
              { k: t.stat_cat, v: t.stat_cat_v },
              { k: t.stat_org, v: t.stat_org_v },
              { k: t.stat_focus, v: t.stat_focus_v, full: true },
            ].map((s, i) => (
              <div key={i} className={`tele-card p-5 ${s.full ? "col-span-2" : ""}`} data-testid={`stat-card-${i}`}>
                <div className="font-mono-alt text-[10px] tracking-[0.3em] text-[#005fca] uppercase">{s.k}</div>
                <div className={`font-display text-2xl mt-2 uppercase ${s.accent === "gold" ? "text-[#fdb429]" : "text-white"}`}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 3. STEM RACING */}
      <Section className="bg-[#022a67]/10 border-y border-[#022a67]">
        <Eyebrow n="02" label={t.eb_stem} />
        <h2 className="h2-section text-4xl md:text-5xl mb-3">{t.h_more_than_race}</h2>
        <p className="text-white/70 max-w-3xl mb-4 text-lg">{t.stem_copy_1}</p>
        <p className="text-[#8ba3cc] max-w-3xl mb-12">{t.stem_copy_2}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {pillars.map((p, i) => {
            const Icon = iconByKey[p.key];
            return (
              <div key={i} className="tele-card p-6 group" data-testid={`stem-pillar-${i}`}>
                <div className="w-11 h-11 mb-4 flex items-center justify-center border border-[#005fca]/40 bg-[#020815] group-hover:border-[#e77300] transition-colors">
                  {Icon && <Icon className="w-5 h-5 text-[#e77300]" />}
                </div>
                <div className="font-display text-lg uppercase tracking-wide">{t[`pillar_${p.key}`]}</div>
                <div className="text-sm text-[#8ba3cc] mt-2 leading-relaxed">{t[`pillar_${p.key}_d`]}</div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* 4. NATIONAL CHAMPIONS */}
      <Section>
        <Eyebrow n="03" label={t.eb_achievement} />
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 border border-[#fdb429]/40 bg-[#fdb429]/5">
              <Trophy className="w-4 h-4 text-[#fdb429]" />
              <span className="font-mono-alt text-xs tracking-[0.3em] text-[#fdb429] uppercase">{t.pill_natchamps}</span>
            </div>
            <h2 className="h2-section text-4xl md:text-6xl mb-6">{t.h_natchamps_a} <span className="text-[#fdb429]">{t.h_natchamps_b}</span></h2>
            <p className="text-white/80 leading-relaxed mb-4">{t.natchamps_1}</p>
            <p className="text-white/70 leading-relaxed mb-8">{t.natchamps_2}</p>
            <blockquote className="border-l-2 border-[#fdb429] pl-6 py-2 mb-8">
              <p className="font-display text-xl md:text-2xl text-white leading-snug uppercase tracking-tight">{t.natchamps_quote}</p>
            </blockquote>
            <div className="space-y-3">
              {[1,2,3,4].map((n, i) => (
                <div key={i} className="flex gap-4 items-center" data-testid={`timeline-${i}`}>
                  <div className="font-mono-alt text-[10px] text-[#e77300] w-6">{String(n).padStart(2, "0")}</div>
                  <div className="w-2 h-2 rotate-45 bg-[#e77300]" />
                  <div><div className="font-display uppercase tracking-wide text-sm">{t[`tl_${n}`]}</div><div className="text-xs text-[#8ba3cc]">{t[`tl_${n}d`]}</div></div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-3">
            <RealImage src="/images/victory/v1.jpg" alt="National Champions Brazil 2026" ratio="3/4" className="col-span-2 md:row-span-2" />
            <RealImage src="/images/victory/v2.jpg" alt="National victory" ratio="1/1" />
            <RealImage src="/images/victory/v3.jpg" alt="National victory" ratio="1/1" />
          </div>
        </div>
      </Section>

      {/* 5. ROAD TO SINGAPORE */}
      <Section className="border-y border-[#022a67] bg-gradient-to-b from-[#022a67]/20 to-transparent">
        <Eyebrow n="04" label={t.eb_global} />
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <h2 className="h2-section text-4xl md:text-6xl mb-6">{t.h_road_a} <span className="text-[#e77300]">{t.h_road_b}</span></h2>
            <p className="text-white/80 leading-relaxed mb-8">{t.road_copy}</p>
            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                { icon: MapPin, label: t.road_travel, d: t.road_travel_d },
                { icon: Rocket, label: t.road_prod, d: t.road_prod_d },
                { icon: Target, label: t.road_prep, d: t.road_prep_d },
                { icon: Calendar, label: t.road_camp, d: t.road_camp_d },
              ].map((it, i) => { const I = it.icon; return (
                <div key={i} className="tele-card p-4"><I className="w-4 h-4 text-[#e77300] mb-2" /><div className="font-display text-sm uppercase">{it.label}</div><div className="text-xs text-[#8ba3cc] mt-1">{it.d}</div></div>
              ); })}
            </div>
            <Link to="/sponsorship" className="btn-racing primary" data-testid="home-support-worldfinals"><span>{t.road_cta} <ArrowRight className="w-4 h-4" /></span></Link>
          </div>
          <div className="lg:col-span-6 relative">
            <RealImage src="/images/misc/brazil-singapore-map.jpg" alt="Brazil to Singapore route" ratio="4/3" fit="contain" padded testId="road-map-image" />
            <div className="absolute -top-3 -right-3 tele-card p-3 hidden md:block">
              <div className="font-mono-alt text-[10px] text-[#005fca] tracking-widest">{t.road_distance}</div>
              <div className="font-display text-lg text-[#e77300]">~16,000 KM</div>
            </div>
          </div>
        </div>
      </Section>

      {/* 6. ENGINEERING */}
      <Section>
        <Eyebrow n="05" label={t.eb_eng} />
        <h2 className="h2-section text-4xl md:text-5xl mb-4">{t.h_precision}</h2>
        <p className="text-white/80 max-w-3xl mb-12 text-lg">{t.eng_copy}</p>
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-4">
            <RealImage src="/images/engineering/cad.jpg" alt="CAD wireframe of the race car" ratio="16/9" />
            <div className="grid grid-cols-2 gap-4">
              <RealImage src="/images/engineering/cfd.jpg" alt="CFD simulation" ratio="4/3" />
              <RealImage src="/images/engineering/cnc.jpg" alt="CNC manufacturing" ratio="4/3" />
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            {[
              [t.eng_stat_aero, "CD 0.24"],
              [t.eng_stat_cad, "Fusion 360"],
              [t.eng_stat_cfd, "Ansys"],
              [t.eng_stat_mfg, "CNC + 3D"],
              [t.eng_stat_mat, "Composites"],
              [t.eng_stat_wt, "48 g"],
              [t.eng_stat_test, "Track+Wind"],
              [t.eng_stat_iter, "18 cycles"],
            ].map(([k, v], i) => (
              <div key={i} className="tele-card p-4" data-testid={`eng-stat-${i}`}><div className="font-mono-alt text-[10px] tracking-widest text-[#005fca] uppercase">{k}</div><div className="font-display text-lg mt-1 text-white">{v}</div></div>
            ))}
          </div>
        </div>

        <div className="mt-14">
          <div className="eyebrow mb-4">{t.dev_gallery}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {ENG_GALLERY.map((src, i) => (<RealImage key={i} src={src} alt={`Engineering ${i + 1}`} ratio="4/3" testId={`eng-gal-${i}`} />))}
          </div>
        </div>
      </Section>

      {/* 6b. CAR RENDERS */}
      <Section className="bg-[#022a67]/10 border-y border-[#022a67]">
        <Eyebrow n="06" label={t.eb_renders} />
        <h2 className="h2-section text-4xl md:text-5xl mb-3">{t.h_car}</h2>
        <p className="text-white/70 max-w-2xl mb-10">{t.renders_copy}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {RENDERS.map((src, i) => (
            <RealImage key={i} src={src} alt={`Alight Racing render ${i + 1}`} ratio={i === 0 || i === 4 ? "16/10" : "4/3"} className={i === 0 ? "md:col-span-2" : ""} fit="cover" testId={`render-${i}`} />
          ))}
        </div>
      </Section>

      {/* 6c. PIT DISPLAY */}
      <Section>
        <Eyebrow n="07" label={t.eb_pit} />
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <h2 className="h2-section text-4xl md:text-5xl mb-4">{t.h_pit}</h2>
            <p className="text-white/80 mb-6">{t.pit_copy}</p>
            <RealImage src="/images/pit/pit-main.jpg" alt="Alight Racing pit display" ratio="16/10" testId="pit-main" />
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            {PIT_GALLERY.map((src, i) => (<RealImage key={i} src={src} alt={`Pit display ${i + 1}`} ratio="1/1" testId={`pit-gal-${i}`} />))}
          </div>
        </div>
      </Section>

      {/* 7. ENTERPRISE */}
      <Section className="bg-[#022a67]/10 border-y border-[#022a67]">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Eyebrow n="08" label={t.eb_enterprise} />
            <h2 className="h2-section text-4xl md:text-5xl mb-6">{t.h_value}</h2>
            <p className="text-white/80 leading-relaxed mb-6">{t.ent_copy}</p>
            <Link to="/sponsorship" className="btn-racing primary" data-testid="home-partner-with-us"><span>{t.ent_cta} <ArrowRight className="w-4 h-4" /></span></Link>
          </div>
          <div className="lg:col-span-5 grid grid-cols-1 gap-2">
            {[1,2,3,4,5,6,7,8,9,10].map((i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-[#022a67]" data-testid={`spo-benefit-${i-1}`}>
                <div className="w-1.5 h-1.5 bg-[#e77300]" /><span className="text-sm text-white/80">{t[`ent_b_${i}`]}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 8. IMPACT */}
      <Section>
        <Eyebrow n="09" label={t.eb_impact} />
        <h2 className="h2-section text-4xl md:text-5xl mb-4">{t.h_purpose}</h2>
        <p className="text-white/80 max-w-3xl mb-12 text-lg">{t.impact_copy}</p>
        <div className="grid md:grid-cols-3 gap-4">
          {[1,2,3].map((n, i) => (
            <div key={i} className="tele-card p-6" data-testid={`impact-card-${i}`}>
              <div className="font-display text-xl uppercase mb-2">{t[`imp_${n}t`]}</div>
              <p className="text-sm text-[#8ba3cc]">{t[`imp_${n}d`]}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 9. TEAM PREVIEW */}
      <Section className="border-t border-[#022a67]">
        <Eyebrow n="10" label={t.eb_team} />
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <h2 className="h2-section text-4xl md:text-5xl">{t.h_team}</h2>
          <Link to="/about#team" className="btn-racing secondary" data-testid="home-meet-team"><span>{t.home_meet_team}</span></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TEAM.map((m) => (
            <div key={m.id} className="tele-card overflow-hidden" data-testid={`team-card-${m.id}`}>
              <RealImage src={TEAM_PHOTOS[m.id]} alt={m.name} ratio="1/1" fit="cover" frameless className="border-b border-[#005fca]/30" />
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display text-xl uppercase leading-none">{m.name}</div>
                  <div className="font-mono-alt text-xs text-[#e77300]">#{m.number}</div>
                </div>
                <div className="text-[10px] tracking-[0.3em] text-[#005fca] uppercase font-mono-alt">{roleByTeam[m.id]}</div>
                <p className="text-xs text-[#8ba3cc] mt-3 leading-relaxed">{bioByTeam[m.id]}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 10. PARTNERS */}
      <Section className="bg-[#022a67]/10 border-y border-[#022a67]">
        <Eyebrow n="11" label={t.eb_sponsors} />
        <h2 className="h2-section text-4xl md:text-5xl mb-4">{t.h_partners}</h2>
        <p className="text-white/70 max-w-2xl mb-10">{t.partners_copy}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-10">
          {SPONSORS.map((s, i) => (
            <div key={i} className="tele-card p-6 flex items-center justify-center h-28 md:h-32 group" data-testid={`sponsor-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} title={s.name}>
              <img src={s.src} alt={s.name} className="max-h-full max-w-full object-contain group-hover:brightness-125 transition" style={{ filter: "brightness(1.1)" }} />
            </div>
          ))}
        </div>
        <div className="flex justify-center">
          <Link to="/sponsorship" className="btn-racing primary" data-testid="home-join-partner-network"><span>{t.partners_cta} <ArrowRight className="w-4 h-4" /></span></Link>
        </div>
      </Section>

      {/* 11. SHOP PREVIEW */}
      <Section>
        <Eyebrow n="12" label={t.eb_shop} />
        <h2 className="h2-section text-4xl md:text-5xl mb-4">{t.h_build}</h2>
        <p className="text-white/70 max-w-2xl mb-10">{t.shop_copy}</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {[1,2,3,4,5].map((n, i) => (
            <div key={i} className="tele-card p-6 text-center" data-testid={`shop-preview-${i}`}>
              <div className="w-8 h-8 mx-auto mb-3 border border-[#005fca]/50 rotate-45 flex items-center justify-center">
                <span className="-rotate-45 font-mono-alt text-xs text-[#e77300]">{String(n).padStart(2, "0")}</span>
              </div>
              <div className="font-display text-sm uppercase tracking-wide">{t[`shop_cat_${n}`]}</div>
            </div>
          ))}
        </div>
        <div className="flex justify-center">
          <Link to="/sponsorship" className="btn-racing primary" data-testid="home-open-shop"><span>{t.shop_cta} <ArrowRight className="w-4 h-4" /></span></Link>
        </div>
      </Section>

      {/* 12. CONTACT PREVIEW */}
      <Section className="border-t border-[#022a67]">
        <Eyebrow n="13" label={t.eb_contact} />
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="h2-section text-4xl md:text-5xl mb-6">{t.h_together}</h2>
            <p className="text-white/80 mb-6">{t.together_copy}</p>
            <div className="space-y-3 text-white/80 text-sm">
              <div><span className="eyebrow block mb-1">{t.lb_email}</span><a className="text-white hover:text-[#e77300]" href="mailto:Partnerships@alightracing.com">Partnerships@alightracing.com</a></div>
              <div><span className="eyebrow block mb-1">{t.lb_location}</span>{t.footer_location}</div>
            </div>
          </div>
          <div className="tele-card p-6">
            <div className="eyebrow mb-3">{t.lb_direct}</div>
            <p className="text-white/80 text-sm mb-4">{t.direct_copy}</p>
            <Link to="/contact" className="btn-racing primary" data-testid="home-contact-us"><span>{t.go_contact} <ArrowRight className="w-4 h-4" /></span></Link>
          </div>
        </div>
      </Section>
    </>
  );
}
